import { IconButton, Tooltip, type SxProps, type Theme } from '@mui/material';
import { VolumeOff, VolumeUp } from '@mui/icons-material';
import { presentationPreviewHasVideoAudio } from '@/helpers/preview/presentation-video-overlays.helper';
import { useTranslations } from '@/i18n/use-translations';

export interface PreviewAudioToggleButtonProps {
  audioEnabled: boolean;
  onToggle: () => void;
  /**
   * `overlay` — absolute bottom-right inside a preview frame (legacy).
   * `inline` — sits in a toolbar row (e.g. next to Stage/Broadcast tabs).
   */
  variant?: 'overlay' | 'inline';
  sx?: SxProps<Theme>;
}

/** Local preview listen toggle — does not control live playout. */
export function PreviewAudioToggleButton({
  audioEnabled,
  onToggle,
  variant = 'overlay',
  sx,
}: PreviewAudioToggleButtonProps) {
  const t = useTranslations();
  const title = audioEnabled
    ? t('flows.detail.perform.mutePreviewAudio', undefined, 'Mute preview audio')
    : t('flows.detail.perform.unmutePreviewAudio', undefined, 'Listen to preview audio');

  return (
    <Tooltip title={title}>
      <IconButton
        onClick={onToggle}
        size="small"
        sx={{
          ...(variant === 'overlay'
            ? {
                'position': 'absolute',
                'bottom': 8,
                'right': 8,
                'zIndex': 1280,
                'bgcolor': 'rgba(0,0,0,0.6)',
                'color': 'white',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
              }
            : { color: 'text.secondary' }),
          ...sx,
        }}
        aria-label={title}
      >
        {audioEnabled ? <VolumeUp fontSize="small" /> : <VolumeOff fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
}

export function isPreviewAudioCapableLayer(layer: { componentId: string; data?: Record<string, string> }): boolean {
  const { componentId, data = {} } = layer;
  if (componentId === 'audio') {
    return !!(data.audioUrl || data.url);
  }
  if (componentId === 'video' || componentId === 'backgroundVideo') {
    return !!(data.videoUrl || data.url);
  }
  if (componentId === 'imageSequence' || componentId === 'presentationSlides') {
    return presentationPreviewHasVideoAudio(data.videoOverlays);
  }
  return false;
}
